import { create } from "zustand";
import axios from "axios";

// Create authenticated axios instance
const authAxios = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});

// Request interceptor to add auth token
authAxios.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  const tenantId = localStorage.getItem("tenantId");
  const role = localStorage.getItem("role");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (tenantId) {
    config.params = config.params || {};
    config.params.tenantId = tenantId;
    if (role === "SuperAdmin" && tenantId) {
      config.params.allTenants = true; // SuperAdmin can bypass tenancy
    }
  }
  return config;
});

// Response interceptor to handle errors
authAxios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Handle unauthorized error (e.g., redirect to login)
      console.error("Authentication failed - redirecting to login");
      window.location.href = "/";
    }
    return Promise.reject(error);
  }
);

const useSurveyManagementStore = create((set) => ({
  surveys: [],
  totalDistance: 0,
  overallSeverityAll: [],
  defectAnalysisAll: [],
  budgetReportById: [],
  currencies: [],
  selectedCurrency: "AED",
  isLoading: false,
  error: null,
  selectedSurveyDetails: null, // New state to store specific survey details

  // Helper function to handle errors consistently
  handleError: (error, action) => {
    const errorMessage =
      error.response?.data?.message || error.message || `Failed to ${action}`;
    console.error(`Error ${action}:`, error);
    set({ error: errorMessage, isLoading: false });
    throw errorMessage;
  },

  fetchSurveys: async () => {
    try {
      set({ isLoading: true, error: null });
      const response = await authAxios.get("api/surveys");
      set({ surveys: response.data, isLoading: false });
    } catch (error) {
      useSurveyManagementStore.getState().handleError(error, "fetch surveys");
    }
  },

  // New method to fetch specific survey details
  fetchSurveyDetails: async (surveyId) => {
    try {
      set({ isLoading: true, error: null, selectedSurveyDetails: null });
      const summaryResponse = await authAxios.get(`api/surveys/summary/${surveyId}`);
      const { totalDistance, overallSeverity, severityDistribution, defectAnalysis } = summaryResponse.data;
      console.log("Fetched survey details:", summaryResponse.data); // Add this log to debu
      set({
        selectedSurveyDetails: {
          totalDistance,
          overallSeverity,
          severityDistribution,
          defectAnalysis,
        },
        isLoading: false,
      });
    } catch (error) {
      useSurveyManagementStore.getState().handleError(error, "fetch survey details");
    }
  },

  createRoadSurvey: async (surveyData) => {
    try {
      set({ isLoading: true, error: null });
      const role = localStorage.getItem("role");
      // Add tenantId to surveyData from localStorage
      const tenantId = localStorage.getItem("tenantId");
      const finalSurveyData = {
        ...surveyData,
        tenantId: role === "SuperAdmin" ? null : tenantId || undefined, // Send null for SuperAdmins, tenantId for others
        currency: useSurveyManagementStore.getState().selectedCurrency,
      };
      const response = await authAxios.post("api/surveys/create", finalSurveyData);
      set((state) => ({
        surveys: [...state.surveys, response.data.survey],
        isLoading: false,
      }));
      return response.data;
    } catch (error) {
      return useSurveyManagementStore
        .getState()
        .handleError(error, "create survey");
    }
  },

  deleteSurvey: async (surveyId) => {
    try {
      set({ isLoading: true, error: null });
      await authAxios.delete(`api/surveys/${surveyId}`);
      set((state) => ({
        surveys: state.surveys.filter((survey) => survey._id !== surveyId),
        isLoading: false,
      }));
    } catch (error) {
      useSurveyManagementStore.getState().handleError(error, "delete survey");
    }
  },

  searchByRoadName: async (roadName) => {
    try {
      if (!roadName.trim()) {
        return useSurveyManagementStore.getState().fetchSurveys();
      }

      set({ isLoading: true, error: null });
      const response = await authAxios.get("api/surveys/search/road", {
        params: { name: roadName },
      });
      set({ surveys: response.data, isLoading: false });
    } catch (error) {
      useSurveyManagementStore.getState().handleError(error, "search surveys");
    }
  },

  downloadReport: async () => {
    try {
      set({ isLoading: true, error: null });
      const response = await authAxios.get("api/surveys/download-report", {
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "survey-management-report.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      set({ isLoading: false });
    } catch (error) {
      useSurveyManagementStore.getState().handleError(error, "download report");
    }
  },

  totalDistanceAll: async () => {
    try {
      set({ isLoading: true, error: null });
      const response = await authAxios.get("api/surveys/total-distance-all");
      set({ totalDistance: response.data, isLoading: false });
    } catch (error) {
      useSurveyManagementStore
        .getState()
        .handleError(error, "fetch total distance");
    }
  },

  overallSeverity: async () => {
    try {
      set({ isLoading: true, error: null });
      const response = await authAxios.get("api/surveys/overall-severity-all");
      set({ overallSeverityAll: response.data, isLoading: false });
    } catch (error) {
      useSurveyManagementStore
        .getState()
        .handleError(error, "fetch overall severity");
    }
  },

  defectAnalysis: async () => {
    try {
      set({ isLoading: true, error: null });
      const response = await authAxios.get("api/surveys/defect-analysis-all");
      set({ defectAnalysisAll: response.data, isLoading: false });
    } catch (error) {
      useSurveyManagementStore
        .getState()
        .handleError(error, "fetch defect analysis");
    }
  },

  fetchCurrencies: async () => {
    try {
      set({ isLoading: true, error: null });
      const response = await authAxios.get("api/surveys/currencies");
      set({ currencies: response.data, isLoading: false });
    } catch (error) {
      useSurveyManagementStore.getState().handleError(error, "fetch currencies");
    }
  },

  setCurrency: (currency) => {
    set({ selectedCurrency: currency });
  },

  budgetReport: async (surveyId, costs = {}) => {
    try {
      set({ isLoading: true, error: null });
      const selectedCurrency = useSurveyManagementStore.getState().selectedCurrency;
      const response = await authAxios.get(`api/surveys/budget/${surveyId}`, {
        params: {
          costs: JSON.stringify(costs),
          currency: selectedCurrency,
        },
      });
      set({ budgetReportById: response.data, isLoading: false });
    } catch (error) {
      useSurveyManagementStore
        .getState()
        .handleError(error, "fetch budget report");
    }
  },

  downloadBudgetReportById: async (surveyId, costs = {}) => {
    try {
      set({ isLoading: true, error: null });
      const selectedCurrency = useSurveyManagementStore.getState().selectedCurrency;
      const response = await authAxios.get(`api/surveys/budget/${surveyId}`, {
        params: {
          download: true,
          costs: JSON.stringify(costs),
          currency: selectedCurrency,
        },
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `budget-report-${surveyId}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      set({ isLoading: false });
    } catch (error) {
      useSurveyManagementStore
        .getState()
        .handleError(error, "download budget report");
    }
  },

  // Clear error state
  clearError: () => set({ error: null }),
}));

export default useSurveyManagementStore;